import { Injectable } from '@angular/core';
import { map, switchMap, tap } from 'rxjs/operators';
import { forkJoin, from, Observable, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import cloneDeep from 'lodash/cloneDeep';

import { OrderService } from './order.service';
import { ModalService } from './modal.service';
import { OrderItem } from '../models/order-item.model';
import { Order } from '../models/order.model';
import { AuthService } from './auth.service';
import { AlertType } from '../enums/alert-type';
import { Address } from '../models/address.model';
import { Product } from '../models/product.model';
import { AddressService } from './address.service';
import { BudgetService } from './budget.service';
import { OrderItemDelivery } from '../models/order-item-delivery.model';
import {
  checkForUniqueShipmentAddresses,
  computeAvailableAddresses,
  isPaymentMethodDefined,
} from '../shared/helpers';
import { ProductService } from './product.service';
import { isEmptyArray } from '../shared/utils';
import { Budget } from '../models/budget.model';
import { ErrorCodes } from '../enums/error.codes';
import { PaymentService } from './payments.service';
import { MobileCheckServiceService } from './mobile-check-service.service';
import { Router } from '@angular/router';
import { OrderWindow } from '../models/order-window.model';
import { OrderTypes } from '../enums/order-types';
import { EntityService } from './entity.service';
import { RecommendedProductsService } from './recommended-products.service';

type SaveOrderItemResult = {
  purchaseResult: { added: boolean; updated: boolean; deleted: boolean };
  order: Order;
  orderItem: OrderItem;
  budgets?: Budget[];
};

@Injectable()
export class OrderItemService {
  updating = false;

  constructor(
    private orderService: OrderService,
    private productService: ProductService,
    private modalService: ModalService,
    private authService: AuthService,
    private addressService: AddressService,
    private budgetService: BudgetService,
    private toastr: ToastrService,
    private paymentService: PaymentService,
    private mobileCheck: MobileCheckServiceService,
    private router: Router,
    private entityService: EntityService,
    private recommendedService: RecommendedProductsService
  ) {}

  private checkPaymentDefined(
    order: Order,
    showOrderSettings = false
  ): Observable<Order> {
    if (!order || !order.id || order.limited) {
      return of(null);
    }

    // check if order has cost center
    if (
      !isPaymentMethodDefined(
        order,
        this.authService.user,
        this.authService.canEditSAP
      )
    ) {
      return from(
        this.modalService.showSAPModal(order, showOrderSettings, true)
      ).pipe(
        map((retOrder) => {
          // check order payment after order settings update
          if (
            isPaymentMethodDefined(
              retOrder,
              this.authService.user,
              this.authService.canEditSAP
            )
          ) {
            return retOrder;
          }
          return null;
        })
      );
    } else {
      return of(order);
    }
  }

  public saveOrderItem(
    order: Order,
    orderItem: OrderItem,
    skipNewOrderItem = false,
    originalOrderItem: OrderItem = null
  ): Observable<SaveOrderItemResult> {
    if (!order || !orderItem || !this.authService.canEditOrder(order)) {
      return of(null);
    }

    orderItem.calculateQuantity();
    const copiedItem = new OrderItem(
      originalOrderItem ? originalOrderItem : orderItem
    );

    const purchaseResult = { added: false, updated: false, deleted: false };

    const orderItemQuantity = orderItem.deliveriesTotalQuantity;
    // if (order.isCustom) {
    //   if (orderItem.product.hasVariations) {
    //
    //     orderItemQuantity =
    //       orderItem.skus.reduce((total, sku) => total + sku.quantity, 0) || 0;
    //
    //   } else {
    //     orderItemQuantity = orderItem.deliveriesTotalQuantity;
    //   }
    //
    // } else {
    //   orderItemQuantity = ;
    // }


    // check for zero quantity
    if (!orderItem.id && orderItemQuantity === 0) {
      const msg = `Please add a quantity before clicking ${orderItem.init_quantity > 0 ? 'Update' : 'Add to Cart'}`;
      this.toastr.warning(msg);
      return of({purchaseResult, orderItem, order});
    }

    // check for customizations
    if (!orderItem.id && orderItem.product.isCustomizable) {
      if (!orderItem.isCustomizationDefined) {
        this.toastr.warning('Please customize the product');
        return of({purchaseResult, orderItem, order});
      }
    }

    if (orderItem.product.hasVariations) {
      if (checkForUniqueShipmentAddresses(orderItem) === false) {
        this.toastr.warning(
          'Please select unique shipping addresses for each size.'
        );
        return of({ purchaseResult, orderItem, order });
      }
    }

    return this.checkPaymentDefined(order).pipe(
      switchMap((result) => {
        order = result;
        if (!order) {
          return of(null);
        }

        this.updating = true;

        // orderItem = this.budgetService.computeBudgetAmount(orderItem);

        if (orderItem.id) {
          if (orderItem.quantity === 0) {
            const product = new Product(orderItem.product);
            // delete item
            return this.orderService.deleteOrderItem(order, orderItem).pipe(
              switchMap((retOrder) => {
                if (!retOrder) {
                  // delete failed - restore item
                  return of({ purchaseResult, orderItem: copiedItem, order });
                }
                purchaseResult.deleted = true;
                const wslrEntityId = retOrder.isWSLR ? retOrder.entity_id : 0;

                if (product.isAutoship) {
                  return this.productService
                    .getProduct(product.id, wslrEntityId, false, true)
                    .pipe(
                      map((retProduct) => {
                        // setup new order item
                        this.getProductPurchasedDetails(product.id);
                        const newOrderItem = this.setupOrderItem(
                          order,
                          retProduct || product
                        );
                        this.toastr.success('Your item has been deleted.');
                        return {
                          purchaseResult,
                          orderItem: newOrderItem,
                          order,
                        };
                      })
                    );
                }

                return this.productService
                  .getProductShortInfoById(product.id)
                  .pipe(
                    switchMap((_) => {
                      return this.productService
                        .getProduct(product.id, wslrEntityId, false, true)
                        .pipe(
                          map((retProduct) => {
                            // setup new order item
                            this.getProductPurchasedDetails(product.id);
                            const newOrderItem = this.setupOrderItem(
                              order,
                              retProduct || product
                            );
                            this.toastr.success('Your item has been deleted.');
                            return {
                              purchaseResult,
                              orderItem: newOrderItem,
                              order,
                            };
                          })
                        );
                    })
                  );
              })
            );
          } else {
            // update item
            const orderItemId = orderItem.id;
            const budgetId = orderItem.financials_total?.budget_id;
            return this.orderService.updateOrderItem(order, orderItem).pipe(
              map((retOrder) => {
                if (retOrder) {
                  purchaseResult.updated = true;
                  const updatedOrderItem = retOrder.items.find(
                    (i) => i.id === orderItemId
                  );
                  updatedOrderItem.changed = false;
                  updatedOrderItem.financials_total.budget_id = budgetId;
                  this.getProductPurchasedDetails(
                    updatedOrderItem?.product?.id
                  );
                  this.toastr.success('Your item has been updated');
                  this.productService.updateProductShortInfo(
                    updatedOrderItem.product
                  );
                  return { purchaseResult, orderItem: updatedOrderItem, order };
                } else {
                  return { purchaseResult, orderItem: copiedItem, order };
                }
              }),
              tap((p) => {})
            );
          }
        } else {
          return this.orderService.createOrderItem(order, orderItem).pipe(
            map((retOrderItem) => {
              if (retOrderItem) {
                retOrderItem.changed = false;
                purchaseResult.added = true;
                this.getProductPurchasedDetails(retOrderItem?.product?.id);
                this.toastr.success('Your item has been added');
              }
              return { purchaseResult, orderItem: retOrderItem, order };
            })
          );
        }
      }),
      switchMap((retVal: SaveOrderItemResult) => {
        if (!retVal) {
          return of(retVal);
        }
        return this.budgetService
          .fetchBudgetItemForOrderItem(retVal?.orderItem)
          .pipe(
            map((retOrderItem) => {
              retVal.orderItem = retOrderItem;
              retVal.budgets = this.budgetService.getBudgetsForOrderItem(
                retVal?.orderItem
              );
              return retVal;
            })
          );
      }),
      tap(() => {
        this.updating = false;
      })
    );
  }

  public getOrderItemFor(
    order: Order,
    orderItem: OrderItem,
    product: Product
  ): OrderItem {
    if (orderItem) {
      return orderItem;
    }
    return this.setupOrderItem(order, product);
  }

  public editOrderItemModal(
    order: Order,
    orderItem: OrderItem,
    showOrderSelector = false
  ): Promise<OrderItem> {
    if (this.mobileCheck.mobile) {
      // redirect to product page
      const product = orderItem.product;
      let url = product.productURL;
      if (product.isBuyingWindow && product.window_id !== order.window_id) {
        url = product.getProductUrlForOrderWindow(order.window_id);
      }
      return this.router.navigate([url]).then(() => orderItem);
    }

    let edittedOrderItem: OrderItem = new OrderItem(orderItem);

    const checkForChanges = (updatedOrderItem) => {
      if (!this.authService.canEditOrder(order)) {
        return true;
      }

      if (!updatedOrderItem.changed) {
        return true;
      }

      if (this.hasFailedTransactions) {
        return true;
      }

      return this.showSaveConfirmation().then((result) => {
        if (!result) {
          return true;
        }

        return this.saveOrderItem(order, updatedOrderItem)
          .pipe(
            map((updateResut) => {
              edittedOrderItem = updateResut.orderItem
                ? updateResut.orderItem
                : null;
              return true;
            })
          )
          .toPromise();
      });
    };

    return this.modalService
      .editOrderItemModal(
        order,
        edittedOrderItem,
        checkForChanges,
        showOrderSelector
      )
      .then(() => {
        return edittedOrderItem;
      })
      .catch(() => {
        return edittedOrderItem;
      });
  }

  public showSaveConfirmation(): Promise<boolean> {
    return this.modalService.showAlert({
      type: AlertType.Confirm,
      class: 'modal-sm',
      headerClass: 'state',
      title: 'Save changes',
      message: 'Your have made changes to your order that are not saved.',
      actionLabel: 'Save',
      cancelLabel: 'Skip & Continue',
    });
  }
  public showCannotSaveConfirmation(): Promise<boolean> {
    return this.modalService.showAlert({
      type: AlertType.Confirm,
      class: 'modal-sm',
      headerClass: 'state',
      title: 'Save changes',
      message:
        'Your have made some changes to your order. However they cannot be saved due to exceed QTY.',
      actionLabel: 'Fix Issue(s)',
      cancelLabel: 'Leave Page',
    });
  }

  private setDefaultDeliveries(order: Order, orderItem: OrderItem): OrderItem {

    const isDecisionPointOrder = (o: Order): boolean =>  {
      return o?.isDecisionPoint || false;
    }

    if (order.isCustom) {
      return orderItem;
    }

    if (isDecisionPointOrder(order) ||
      (!isEmptyArray(orderItem.deliveries) && orderItem.deliveries[0].addressDefined)) {
      return orderItem;
    }

    const product = orderItem.product;

    // clean up existing deliveries
    orderItem.deliveries = [];

    const userRoles = {
      canOrder: this.authService.canOrder,
      isEmployee: this.authService.isEmployee,
    };

    const availableAddresses = computeAvailableAddresses(
      this.addressService.addresses,
      order,
      orderItem
    );

    const addresses = product.isInternationalShipmentAllowed
      ? availableAddresses
      : availableAddresses.filter((a) => a.isInternational !== true);

    // for delivery initializing, create new delivery for  each  sku
    const   sku = product.firstSku;
    if (!sku) {
      return orderItem;
    }

    const delivery = new OrderItemDelivery({
      type: 'SHIPPING',
      method: product.firstAvailableShippingMethod,
      quantity: 0,
      data: {},
      sku_id: sku.id,
    });

    if (order.isWSLR) {
      // set default WLS address
      const entityAddress = Address.fromEntityLocation(
        order.entity.getDefaultShippingLocation()
      );
      if (entityAddress) {
        delivery.setAddress(
          entityAddress,
          this.authService.user,
          order.isCustom,
          userRoles
        );
      }
    } else {
      // set user's default address
      if (!isEmptyArray(addresses)) {
        delivery.setAddress(
          addresses[0],
          this.authService.user,
          order.isCustom,
          userRoles
        );
      }
    }

    orderItem.deliveries.push(delivery);
    return orderItem;
  }


  public setupOrderItem(order: Order, product: Product): OrderItem {
    if (!order || !product) {
      return null;
    }

    let orderItem = order.getItemForProduct(product, null);

    if (this.canOrder(order)) {

      orderItem = this.setDefaultDeliveries(order, orderItem);

      // const userRoles = {
      //   canOrder: true,
      //   isEmployee: this.authService.isEmployee,
      // };
      // // Select first address
      // if (
      //   orderItem.deliveries.length > 0 &&
      //   !orderItem.deliveries[0].street_1
      // ) {
      //   if (order.isWSLR) {
      //     // set default WLS address
      //     const entityAddress = Address.fromEntityLocation(
      //       order.entity.getDefaultShippingLocation()
      //     );
      //     if (entityAddress) {
      //       orderItem.deliveries[0].setAddress(
      //         entityAddress,
      //         this.authService.user,
      //         order.isCustom,
      //         userRoles
      //       );
      //     }
      //     // this.setInitialDeliveryAddress(this._orderItem.deliveries[0], entityAddress);
      //   } else {
      //     // set user's default address
      //     const availableAddresses = computeAvailableAddresses(
      //       this.addressService.addresses,
      //       order,
      //       orderItem
      //     );
      //     if (!isEmptyArray(availableAddresses)) {
      //       orderItem.deliveries[0].setAddress(
      //         availableAddresses[0],
      //         this.authService.user,
      //         order.isCustom,
      //         userRoles
      //       );
      //     }
      //   }
      // }
      this.findBudget(orderItem);
    }

    return orderItem;
  }

  private findBudget(orderItem: OrderItem) {
    const budgetId = orderItem?.financials_total?.budget_id;
    if (budgetId === -1) {
      orderItem.budgetItem = this.budgetService.emptyBudget;
    } else if (orderItem.financials_total.budget_id > 0) {
      orderItem.budgetItem = this.budgetService.budgets.find(
        (b) => b.id === orderItem.financials_total.budget_id
      );
    } else {
      const hardLimitBudget = this.budgetService.getHardlimitBudgetForProduct(
        orderItem.product
      );
      if (hardLimitBudget) {
        orderItem.financials_total.budget_id = hardLimitBudget.id;
        orderItem.budgetItem = hardLimitBudget;
      }
    }
  }

  addAnotherAddress(orderItem: OrderItem, order: Order): OrderItem {
    if (!orderItem || !this.canOrder(order)) {
      return orderItem;
    }

    const hasVariations = orderItem.product.hasVariations;

    const availableAddresses = computeAvailableAddresses(
      this.addressService.addresses,
      order,
      orderItem,
      null,
      hasVariations
    );

    if (isEmptyArray(availableAddresses)) {
      return orderItem;
    }

    const firstAvailableAddress = availableAddresses[0];

    // check if this address has pendingDelete = true otherwise create a new one
    const newDelivery = new OrderItemDelivery({
      type: 'SHIPPING',
      quantity: 0,
      data: {},
      method: orderItem.product.firstAvailableShippingMethod,
      sku_id: orderItem.product.firstSku?.id,
    });
    newDelivery.setAddress(
      firstAvailableAddress,
      this.authService.user,
      order.isCustom,
      { canOrder: true, isEmployee: this.authService.isEmployee }
    );
    orderItem.updateDelivery(newDelivery);
    return new OrderItem(orderItem);
  }

  public calculateDecisionPoints(
    order: Order,
    orderItem: OrderItem,
    quantity
  ): Observable<OrderItem> {
    if (!this.canOrder(order)) {
      return of(orderItem);
    }

    return this.productService
      .calculateDecisionPoints(
        order.attr_decision_point_id,
        orderItem.product.id,
        quantity
      )
      .pipe(
        map((result) => {
          if (isEmptyArray(result)) {
            return orderItem;
          }

          result.forEach((i) => {
            const address = new Address(i.address);

            let delivery = orderItem.findDelivery(address);
            if (delivery) {
              delivery.quantity = i.quantity;
            } else {
              delivery = new OrderItemDelivery({
                quantity: i.quantity,
                type: 'SHIPPING',
                method: orderItem.product.firstAvailableShippingMethod,
                is_uneligible: i.is_uneligible,
              });
              delivery.setAddress(address, this.authService.user, false, {
                canOrder: true,
                isEmployee: this.authService.isEmployee,
              });
            }

            orderItem.updateDelivery(delivery);
          });
          return orderItem;
        })
      );
  }

  public checkout(order: Order): Observable<Order> {
    if (!order) {
      return of(order);
    }

    return this.orderService.checkout(order, true).pipe(
      map((retOrder) => {
        if (retOrder) {
          if (
            [
              ErrorCodes.CREDIT_CARD_PAYMENT_ERROR_CODE,
              ErrorCodes.CREDIT_CARD_AMOUNT_EXCEEDED_ERROR_CODE,
            ].includes(retOrder.error?.errorCode)
          ) {
            const transactionId = retOrder.error.data.transactionId;
            this.modalService.showCreditCardPaymentFailed(
              order,
              transactionId,
              retOrder.error.errorCode
            );
          }
        }
        return retOrder;
      })
    );
  }

  public get hasFailedTransactions(): boolean {
    return this.paymentService.hasFailedTransactions;
  }

  public canOrder(order: Order): boolean {
    return this.authService.canEditOrder(order);
  }

  public hasAccessToProduct(
    order: Order,
    product: Product
  ): Observable<boolean> {
    if (!order || !order.loaded || !product) {
      return of(false);
    }

    // check only for wslr order
    if (!order.isWSLR) {
      return of(true);
    }

    return of(true);
    // return this.productService.getProduct(product.id, order.entity_id, false, true).pipe(
    //     map( retOrder => {
    //         return !!retOrder;
    //     })
    // );
  }

  private getOrCreateOrder(
    orderId,
    orderType,
    canCreateOrder
  ): Observable<Order> {
    if (orderId) {
      return this.orderService.getOrder(orderId);
    }

    if (canCreateOrder) {
      return this.orderService.createOrder(orderType).pipe(
        switchMap((retOrder) => {
          if (!retOrder) {
            return of(retOrder);
          }
          return this.checkPaymentDefined(retOrder, true);
        })
      );
    }

    return of(null);
  }

  public shopProduct(
    order: Order,
    product: Product,
    canCreateOrder = false,
    showOrderSelector = false
  ) {
    if (!order || !product) {
      return;
    }

    this.productService
      .getProduct(product.id, order.entity_id, false, true)
      .pipe(
        switchMap((retProduct) => {
          if (!retProduct) {
            return of({ shopOrder: null, shopProduct: null });
          }
          return this.getOrCreateOrder(
            order.id,
            order.type,
            canCreateOrder
          ).pipe(
            map((retOrder) => {
              return { shopOrder: retOrder, shopProduct: retProduct };
            })
          );
        }),
        switchMap(({ shopOrder, shopProduct }) => {
          order = shopOrder;
          if (!shopOrder || !shopOrder?.id || !shopProduct) {
            return of({ shopOrder: null, shopProduct: null, hasAccess: false });
          }
          return this.hasAccessToProduct(order, shopProduct).pipe(
            map((result) => ({ shopOrder, shopProduct, hasAccess: result }))
          );
        }),
        switchMap(({ shopOrder, shopProduct, hasAccess }) => {
          if (hasAccess && shopProduct && shopOrder) {
            this.productService.populateProduct(shopProduct);
            const orderItem = this.getOrderItemFor(
              shopOrder,
              null,
              shopProduct
            );
            const retOrderItem = this.getOrderItemFor(
              shopOrder,
              orderItem,
              shopProduct
            );
            return from(
              this.editOrderItemModal(order, retOrderItem, showOrderSelector)
            );
          } else {
            return of(null);
          }
        })
      )
      .subscribe(() => {});
  }

  public showProductInModal(
    product: Product,
    order?: Order,
    showOrderSelector = false
  ): Observable<any> {
    if (!product) {
      return of(null);
    }

    let showingProduct: Product = product;
    return this.getActualProduct(product).pipe(
      tap((retProduct) => (showingProduct = retProduct)),
      switchMap(() => this.validateProduct(showingProduct)),
      switchMap((valid) => {
        let clonedOrderItem = this.getClonedOrderItem(
          showingProduct,
          order,
          valid
        );
        if (!valid) {
          // if product cannot be purchasable - show as readonly modal
          return this.showModalWindow(clonedOrderItem, order, true);
        }

        return this.getActiveOrder(showingProduct).pipe(
          switchMap((retOrder) => {
            if (retOrder && retOrder.containsProduct(showingProduct)) {
              clonedOrderItem = this.getClonedOrderItem(
                showingProduct,
                retOrder,
                true
              );
            }
            return this.showModalWindow(
              clonedOrderItem,
              retOrder,
              false,
              showOrderSelector
            );
          })
        );
      })
    );
  }

  public prefetchCapexForEntity(orderItem: OrderItem) {
    if (!orderItem || !orderItem.product) {
      return;
    }

    // prefetch only for employees
    if (!this.authService.isEmployee) {
      return;
    }

    const { product } = orderItem;
    if (product.hasLease) {
      const entityIds: number[] = orderItem.deliveries
        .filter(
          (delivery) =>
            delivery.data.entity_id > 0 &&
            delivery.data.entity_id !== this.authService.user.entity_id
        )
        .map((delivery) => delivery.data.entity_id);

      if (isEmptyArray(entityIds)) {
        return;
      }
      forkJoin(
        entityIds.map((id) => this.entityService.prefetchCapex(id))
      ).subscribe((data) => {});
    }
  }

  public checkLeaseStatus(): Observable<string> {
    return this.entityService.reloadCapex(this.authService.user.entity_id);
  }

  public shouldShowLeaseWarning(product: Product) {
    if (!product.hasLease) {
      return false;
    }

    if (this.authService.isEmployee) {
      return false;
    }

    return product.attr_capex_type !== this.authService.user.entity.capex;
  }

  public isProductRecommended(product: Product, order: Order = null): boolean {
    if (!product) {
      return null;
    }
    if (this.authService.isWholeSaler) {
      return product.is_recommended;
    }

    if (this.authService.isEmployee) {
      return (
        product.is_recommended && (order?.isWSLR || order?.isDecisionPoint)
      );
    }
  }

  public canShowRecommendsProducts(order: Order): boolean {
    if (!order) {
      return false;
    }
    if (order.isByingWindow) {
      if (this.authService.isWholeSaler) {
        return true;
      }

      if (this.authService.isEmployee) {
        return order.isWSLR || order.isDecisionPoint;
      }
    }

    return false;
  }

  public showRecommendedProductExportResult(exportType: 'pdf' | 'xlsx') {
    this.modalService.showRecommendedProductExportResult(exportType);
  }

  private getClonedOrderItem(
    product: Product,
    order: Order,
    withReset = false
  ): OrderItem {
    const originalOrderItem = order.getItemForProduct(product);
    let clonedOrderItem = new OrderItem(originalOrderItem);
    clonedOrderItem = this.convertLegacySkus(clonedOrderItem);
    if (withReset) {
      clonedOrderItem.resetFinancialTotals();
      delete clonedOrderItem.id;
      clonedOrderItem.deliveries.forEach((d) => {
        delete d.id;
      });
      clonedOrderItem.checkForAvailableItems();
    }

    return clonedOrderItem;
  }

  //  check is  order item is still have  legacy skus
  private  isLegacySkus(orderItem: OrderItem): boolean {
    if (!orderItem
      || !orderItem.product
      || isEmptyArray(orderItem.product.skus
      || isEmptyArray(orderItem.skus
      || isEmptyArray(orderItem.deliveries)))) {
      return false;
    }

    //  count  skus in order item with qty > 0
    const orderItemSkusLength = orderItem.skus.length;


    // count  deliveries skus in order item with qty > 0
    const deliverySkusLength = orderItem.deliveries.filter(d => d.quantity > 0).length;

    // in legacy  skus  delivery is always  1 and sku > 1
    return  deliverySkusLength === 1 && orderItemSkusLength > 1;
  }

  private convertLegacySkus(orderItem: OrderItem): OrderItem {
    if (!this.isLegacySkus(orderItem)) {
      //  no legacy skus - return orderItem;
      return orderItem;
    }

    const  previousDelivery = orderItem.deliveries[0];


    //  create new delivery for each sku
    const newDeliveries: OrderItemDelivery[] = [];
    for (const orderItemSku of orderItem.skus) {
      if (orderItemSku.quantity > 0)  {
        const newDelivery = cloneDeep(previousDelivery);
        newDelivery.sku_id = orderItemSku.sku_id;
        newDelivery.quantity = orderItemSku.quantity;
        newDeliveries.push(newDelivery);
      }
    }

    orderItem.deliveries = newDeliveries;

    return orderItem;
  }



  // check if there is a product replacement
  private getActualProduct(product: Product): Observable<Product> {
    if (!product.replacedProductId) {
      return of(product);
    }

    return this.productService.getProduct(product.replacedProductId);
  }

  // validate product with order window. For OD order window is 0
  private validateProduct(product: Product): Observable<boolean> {
    if (!product) {
      return of(null);
    }

    // check if product canbe reordered
    if (!product.productActive) {
      return of(false);
    }

    let orderWindow: OrderWindow = null;
    if (product.isBuyingWindow) {
      if (!this.orderService.state.activeOrderWindow) {
        return of(null);
      }
      orderWindow = this.orderService.state.activeOrderWindow;
    }

    // check if product is still can buy
    return this.productService.validateProduct(product, orderWindow);
  }

  private getOrderByType(type: string): Observable<Order> {
    const order =
      type === OrderTypes.ECOMMERCE_TYPE
        ? this.orderService.activeODOrder
        : this.orderService.activeBWOrder;
    if (order) {
      return of(order);
    }
    return this.orderService.createOrder(type);
  }

  private getActiveOrder(product: Product): Observable<Order> {
    if (!product) {
      return of(null);
    }
    const orderType = product.isOnDemand
      ? OrderTypes.ECOMMERCE_TYPE
      : OrderTypes.PREORDER_TYPE;
    return this.getOrderByType(orderType).pipe(
      switchMap((retOrder) => {
        if (!this.authService.canEditOrder(retOrder)) {
          return of(null);
        }
        return this.checkPaymentDefined(retOrder, true);
      })
    );
  }

  private showModalWindow(
    orderItem: OrderItem,
    order: Order,
    readonly = false,
    showOrderSelector = false
  ) {
    if (!orderItem || !order) {
      return of(null);
    }

    if (readonly) {
      return from(this.modalService.showReadonlyProductModal(order, orderItem));
    }

    return from(this.editOrderItemModal(order, orderItem, showOrderSelector));
  }

  private getProductPurchasedDetails(productId: number) {
    this.recommendedService.getProductOrdersIfo(productId);
  }
}
